<template>
  <div class="smart-insights" v-if="hasInsights">
    <!-- Header -->
    <div class="si-header" @click="toggleExpanded">
      <div class="si-header-left">
        <i class="la la-magic si-header-icon"></i>
        <span class="si-title">Teamio AI Insights</span>
      </div>
      <div class="si-header-right">
        <span class="si-count">{{ insights.length }} insight{{ insights.length !== 1 ? 's' : '' }}</span>
        <i :class="expanded ? 'la la-angle-up' : 'la la-angle-down'" class="si-chevron"></i>
      </div>
    </div>

    <!-- Insight Cards -->
    <div v-show="expanded" class="si-body">
      <div
        v-for="insight in insights"
        :key="insight.id"
        class="si-card"
        :class="'si-card-' + insight.type"
      >
        <div class="si-card-accent" :class="'si-accent-' + insight.type"></div>
        <div class="si-card-body">
          <div class="si-card-top">
            <div class="si-card-icon" :class="'si-icon-' + insight.type">
              <i :class="'la ' + insight.icon"></i>
            </div>
            <span class="si-card-category">{{ insight.category || insight.type }}</span>
          </div>
          <div class="si-card-headline">{{ insight.headline }}</div>
          <div class="si-card-text">{{ insight.body }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { evaluateInsights } from './insightRules';

export default {
  name: 'SmartInsights',

  props: {
    metrics: {
      type: Object,
      required: true,
    },
    maxInsights: {
      type: Number,
      default: 5,
    },
    contextLabel: {
      type: String,
      default: 'Production',
    },
    customRules: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    const storageKey = `smartinsights-${this.contextLabel}-expanded`;
    const stored = localStorage.getItem(storageKey);
    return {
      expanded: stored !== null ? stored === 'true' : true,
    };
  },

  computed: {
    insights() {
      return evaluateInsights(this.metrics, this.maxInsights, this.customRules);
    },
    hasInsights() {
      return this.insights.length > 0;
    },
  },

  methods: {
    toggleExpanded() {
      this.expanded = !this.expanded;
      const storageKey = `smartinsights-${this.contextLabel}-expanded`;
      localStorage.setItem(storageKey, String(this.expanded));
      this.$emit('toggle', this.expanded);
    },
  },
};
</script>

<style scoped>
.smart-insights {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ===================== HEADER (shared) ===================== */
.si-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s ease;
}
.si-header:hover {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
}
.si-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.si-header-icon {
  font-size: 20px;
  color: #3abf94;
}
.si-title {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: -0.01em;
}
.si-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}
.si-count {
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 3px 10px;
  border-radius: 12px;
}
.si-chevron {
  font-size: 16px;
  color: #94a3b8;
  transition: transform 0.3s ease;
}

/* ===================== BODY ===================== */
.si-body {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.si-card {
  display: flex;
  border-radius: 12px;
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.15s ease;
}
.si-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  transform: translateY(-1px);
}

/* Accent strip on left */
.si-card-accent {
  width: 4px;
  flex-shrink: 0;
}
.si-accent-positive { background: #16a34a; }
.si-accent-warning  { background: #ca8a04; }
.si-accent-neutral  { background: #5b8ff9; }
.si-accent-opportunity { background: #8b5cf6; }

.si-card-body {
  flex: 1;
  padding: 14px 16px;
}

/* Type-tinted backgrounds */
.si-card-positive .si-card-body { background: linear-gradient(135deg, rgba(22,163,74,0.04) 0%, rgba(22,163,74,0.08) 100%); }
.si-card-warning .si-card-body  { background: linear-gradient(135deg, rgba(202,138,4,0.04) 0%, rgba(202,138,4,0.08) 100%); }
.si-card-neutral .si-card-body  { background: linear-gradient(135deg, rgba(91,143,249,0.04) 0%, rgba(91,143,249,0.08) 100%); }
.si-card-opportunity .si-card-body { background: linear-gradient(135deg, rgba(139,92,246,0.04) 0%, rgba(139,92,246,0.08) 100%); }

.si-card-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.si-card-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}
.si-icon-positive    { background: rgba(22,163,74,0.12);  color: #16a34a; }
.si-icon-warning     { background: rgba(202,138,4,0.12);  color: #ca8a04; }
.si-icon-neutral     { background: rgba(91,143,249,0.12); color: #5b8ff9; }
.si-icon-opportunity { background: rgba(139,92,246,0.12); color: #8b5cf6; }

.si-card-category {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
}

.si-card-headline {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 4px;
  letter-spacing: -0.01em;
}

.si-card-text {
  font-size: 13.5px;
  font-weight: 400;
  color: #475569;
  line-height: 1.6;
}

</style>
